// @ts-check
/// <reference path="./typedefs.js" />
/// <reference path="../../../utils/formatters/user/typedefs.js" />

import HttpClient from '@/utils/clients/clientBookMain';
import { buildErrorCollection, buildElement } from '@/utils/formatters/processingOpenApi';

import { convertUserSessionResponse, prepareUserData } from '@/utils/formatters/user/formattedUserSession';
import { SMS_RESENDING_TIMEOUT } from '@/utils/constants/globalConst';

import {
  ERROR_USER_SESSION,
  ERROR_CAPTCHA,
  ERROR_USER_PASSWORD_RECOVERY,
  ERROR_CHECK_USER_PROBLEM,
  ERROR_CALL_ORDER,
  ERROR_USER_PASSWORD_CHANGE,
  ERROR_USER_AUTHORIZED_BY_HASH,
} from '@/utils/constants/userConstants';

import ServiceError from '@/utils/errors/ServiceError';
import { formattedCoupon } from '@/utils/formatters/formattedCoupon';

export default class UserService {
  constructor(config) {
    this.mainHttp = new HttpClient(config);
  }

  /**
   * Возвращает сессионные данные пользователя
   * @param {Object} dataParams
   * @param {boolean} [dataParams.isGetUser=false]
   * @param {boolean} [dataParams.isGetUserStats=false]
   * @param {boolean} [dataParams.isGetUserLoyalty=false]
   * @param {boolean} [dataParams.isGetUserSubscription=false]
   * @param {boolean} [dataParams.isGetUserChildren=false]
   * @param {boolean} [dataParams.isGetUserSubscriptionRubric=false]
   * @param {boolean} [dataParams.isGetUserSubscriber=false]
   * @param {boolean} [dataParams.isServer=false] - происходит ли запрос на сервере
   * @param {boolean} [dataParams.isGetUserSubscriber=false]
   * @return {Promise<unknown>}
   */
  getUserSessions({
    isGetUser = false,
    isGetUserStats = false,
    isGetUserSubscription = false,
    isGetUserSubscriptionRubric = false,
    isGetUserChildren = false,
    isGetUserLoyalty = false,
    isServer = false,
    isGetUserSubscriber = false,
  }) {
    return new Promise((resolve, reject) => {
      const include = [];
      if (isGetUser) include.push('user');
      if (isGetUserStats) include.push('user.userStats');
      if (isGetUserSubscription) include.push('user.subscription');
      if (isGetUserSubscriptionRubric) include.push('user.subscription.rubric');
      if (isGetUserChildren) include.push('user.customerChildren');
      if (isGetUserLoyalty) include.push('user.userLoyalty');
      if (isGetUserSubscriber) include.push('user.subscriber');

      const dataRequest = {};
      if (include.length) dataRequest.include = include.join(',');

      return this.mainHttp.get('/api/v1/internal/user-session/', dataRequest)
        .then(({ data: { data: dataRes } }) => {
          const resFormatted = buildElement(dataRes);
          let res = { sessionId: resFormatted.id };
          if (resFormatted.relationships?.user) {
            res = {
              ...res,
              ...resFormatted.relationships.user,
            };
          }
          return resolve(res);
        })
        .catch((e) => {
          if (isServer) {
            return reject(e);
          }
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Разлогинивает пользователя
   * @return {Promise<unknown>}
   */
  deleteUserSession() {
    return new Promise((resolve, reject) => this.mainHttp.delete('/api/v1/user-session/')
      .then(() => resolve())
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);

          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Начисление баллов за установку приложения на главный экран
   * @return {Promise<unknown>}
   */
  userInstallApp() {
    return new Promise((resolve, reject) => this.mainHttp.post('/api/v1/user/web-app-installed/')
      .then(() => resolve())
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          // знаем точно что ожидаем одну ошибку
          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Проверка доступности email для регистрации
   * @param {string} email
   * @return {Promise<unknown>}
   */
  checkEmailAvailability(email) {
    return new Promise((resolve, reject) => this.mainHttp.post('/api/v1/user/check-email-availability/', {
      EMAIL: email,
    })
      .then(({ data: { data: dataRes } }) => resolve(dataRes))
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);

          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Проверка email на корректность ввода по белому списку
   * @param {string} email
   * @return {Promise<unknown>}
   */
  checkEmailDomain(email) {
    return new Promise((resolve, reject) => this.mainHttp.get('/api/v1/user/check-email-domain/', {
      EMAIL: email,
    })
      .then((res) => {
        const { data: dataRes } = res;
        if (dataRes.status === 'OK') {
          return resolve(true);
        }
        if (dataRes.status === 'ERROR') {
          return resolve(false);
        }
      })
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);

          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Проверка доступности phone для регистрации
   * @param {string} phone
   * @return {Promise<unknown>}
   */
  checkPhoneAvailability(phone) {
    return new Promise((resolve, reject) => this.mainHttp.get('/api/v1/user/check-phone-availability/', {
      phone,
    })
      .then(({ data: { data: dataRes } }) => {
        resolve(dataRes.data?.attributes?.isAvailable);
      })
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Авторизует пользователя по логину и паролю
   * @param {PayloadUserLogin} payload
   * @return {Promise<unknown>}
   */
  userLogin({
    login,
    password,
    captchaToken,
    captchaAction,
    captchaVersion,
    remember = 'Y',
    stickyBasket = 'Y',
  }) {
    return new Promise((resolve, reject) => {
      if (!login) {
        return reject(ERROR_USER_SESSION.notLogin);
      }
      if (typeof login !== 'string') {
        return reject(ERROR_USER_SESSION.notStringLogin);
      }
      if (!password) {
        return reject(ERROR_USER_SESSION.notPassword);
      }
      if (typeof password !== 'string') {
        return reject(ERROR_USER_SESSION.notStringPassword);
      }
      if (!captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      return this.mainHttp.post('/api/v1/user-session/', {
        LOGIN: login,
        PASSWORD: password,
        REMEMBER: remember,
        STICKY_BASKET: stickyBasket,
        captchaToken,
        captchaAction,
        captchaVersion,
      })
        .then(({ data: { data: dataRes } }) => resolve(convertUserSessionResponse(dataRes?.user)))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Регистрирует пользователя
   * @param {PayloadUserRegister} payload
   * @return {Promise<UserSessionResponse>}
   */
  userRegister({
    phone,
    email,
    name,
    secondName,
    lastName,
    personType = 1,
    authorize = 'Y',
    unsubscribed = 0,
    captchaToken,
    captchaAction,
    captchaVersion,
  }) {
    return new Promise((resolve, reject) => {
      const dataRequest = prepareUserData({
        phone,
        email,
        name,
        secondName,
        lastName,
        personType,
        authorize,
        unsubscribed,
        captchaToken,
        captchaAction,
        captchaVersion,
      });

      if (!captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      return this.mainHttp.post('/api/v1/user/', dataRequest)
        .then((res) => {
          const { data: { data: dataRes } } = res;
          return resolve(convertUserSessionResponse(dataRes?.user));
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Авторизовывает пользователя по хешу
   * @param {Object} payload
   * @param {string} payload.hash - хеш для авторизации
   * @return {Promise<boolean|ErrorObject|Error>}
   */
  authorizeByHash({ hash }) {
    return new Promise((resolve, reject) => {
      if (!hash) reject(new ServiceError(`[UserService:userAuthorizeByHash] ${ERROR_USER_AUTHORIZED_BY_HASH.hashUndefined}`));
      if (typeof hash !== 'string') reject(new ServiceError(`[UserService:userAuthorizeByHash] ${ERROR_USER_AUTHORIZED_BY_HASH.notStringHash}`));

      const dataRequest = { hash };
      this.mainHttp.post('/api/v1/user-session-by-hash/', dataRequest)
        .then(({ data: { data: success } }) => resolve(success))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Обновляет пользователя
   * @param {PayloadUserUpdate} payload
   * @return {Promise<unknown>}
   */
  userUpdate({
    phone,
    email,
    name,
    secondName,
    lastName,
    personType = 1,
    authorize = 'Y',
    unsubscribed = 0,
    gender,
    birthday = '',
    children = [],
    captchaToken,
    captchaVersion,
  }) {
    return new Promise((resolve, reject) => {
      const dataRequest = prepareUserData({
        phone,
        email,
        name,
        secondName,
        lastName,
        personType,
        authorize,
        unsubscribed,
        gender,
        birthday,
        children,
        captchaToken,
        captchaVersion,
        captchaAction: 'updateUser',
      });

      if (!captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }

      if (!captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      return this.mainHttp.put('/api/v1/user/', dataRequest)
        .then((res) => {
          const { data: { data: dataRes } } = res;
          // TODO: дописать конвертор пользователя convertUserSessionResponse
          return resolve(dataRes?.user);
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Удаляет ребенка пользователя
   * @param {Object} payload
   * @param {string} payload.customerId
   * @param {string} payload.childrenId
   * @return {Promise<unknown>}
   */
  removeUserChild(payload) {
    return new Promise((resolve, reject) => this.mainHttp.delete(
      `api/v1/customer/${payload.customerId}/children/${payload.childrenId}/`,
    )
      .then(() => resolve())
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  removeMnogoru(coupon) {
    return new Promise((resolve, reject) => {
      this.mainHttp.delete(`/api/v1/sale/order/coupon-mnogoru/${coupon}/`)
        .then(() => resolve())
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            return reject(errors);
          }
          return reject(new ServiceError(e));
        });
    });
  }

  applyMnogoru(coupon) {
    return new Promise((resolve, reject) => {
      this.mainHttp.post('/api/v1/sale/order/coupon-mnogoru/', {
        COUPON: coupon,
      })
        .then(({ data: { data: { coupon: couponNumber } } }) => {
          resolve(
            formattedCoupon({ coupon: couponNumber }),
          );
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  getMnogoru() {
    return new Promise((resolve, reject) => {
      this.mainHttp.get('/api/v1/sale/order/coupon-mnogoru/')
        .then(({ data: { data: { coupon: couponList } } }) => {
          if (formattedCoupon(couponList[0])) {
            resolve(formattedCoupon(couponList[0]));
          } else {
            reject(new Error('Номер карты Много.ру не найден'));
          }
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  /**
   * Обновляет пароль пользователя
   * @param {Object} payload
   * @param {string} [payload.newPassword]
   * @param {string} [payload.newPasswordConfirm]
   * @param {string} [payload.curPassword]
   * @return {Promise<unknown>}
   */
  userPasswordChange({ newPassword, newPasswordConfirm, curPassword }) {
    return new Promise((resolve, reject) => {
      const errorConfirm = !newPasswordConfirm
        || newPasswordConfirm !== newPassword;
      if (!curPassword) {
        return reject(ERROR_USER_PASSWORD_CHANGE.notCurPassword);
      }
      if (!newPassword) {
        return reject(ERROR_USER_PASSWORD_CHANGE.notNewPassword);
      }
      if (errorConfirm) {
        return reject(ERROR_USER_PASSWORD_CHANGE.notNewPasswordConfirm);
      }

      return this.mainHttp.put('/api/v1/user/password-change/', {
        NEW_PASSWORD: newPassword,
        NEW_PASSWORD_CONFIRM: newPasswordConfirm,
        CUR_PASSWORD: curPassword,
      })
        .then(({ data: { data: dataRes } }) => resolve(dataRes?.login))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Восстанавливает пароль пользователя по email
   * @param {PayloadUserPasswordRecovery} payload
   * @return {Promise<unknown>}
   */
  userPasswordRecovery({
    login,
    captchaToken,
    captchaAction,
    captchaVersion,
  }) {
    return new Promise((resolve, reject) => {
      if (!login) {
        return reject(ERROR_USER_PASSWORD_RECOVERY.notLogin);
      }
      if (typeof login !== 'string') {
        return reject(ERROR_USER_PASSWORD_RECOVERY.notStringLogin);
      }
      if (!captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      return this.mainHttp.post('/api/v1/user/password-recovery/', {
        LOGIN: login,
        captchaToken,
        captchaAction,
        captchaVersion,
      })
        .then(({ data: { data: dataRes } }) => resolve(dataRes?.login))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Восстанавлиевает пароль пользователя по email
   * @param {Object} payload
   * @param {string} [payload.PHONE] - Номер телефона
   * @param {string} [payload.captchaToken]
   * @param {string} [payload.captchaAction]
   * @param {string} [payload.captchaVersion] - "rv2" | "rv3"
   * @return {Promise<unknown>}
   */
  userPasswordRecoveryByPhone(payload = {}) {
    return new Promise((resolve, reject) => {
      const { PHONE } = payload;
      if (!PHONE) {
        return reject(ERROR_USER_PASSWORD_RECOVERY.notPhone);
      }

      return this.mainHttp.post(`/api/v1/user/password-recovery/?PHONE=${PHONE}`, payload)
        .then(({ data: { data: dataRes } }) => resolve(dataRes?.login))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Проверяет пользователя, подозрителен ли он
   * @param {PayloadCheckUserProblem} payload
   * @return {Promise<boolean>}
   */
  checkUserProblem({
    phone,
    email,
    name,
    lastName,
  }) {
    return new Promise((resolve, reject) => {
      if (!phone) {
        return reject(ERROR_CHECK_USER_PROBLEM.notPhone);
      }
      if (typeof phone !== 'string') {
        return reject(ERROR_CHECK_USER_PROBLEM.notStringPhone);
      }

      const dataRequest = {
        PHONE: phone,
      };
      if (email) {
        Object.assign(dataRequest, {
          EMAIL: email,
        });
      }
      if (name) {
        Object.assign(dataRequest, {
          NAME: name,
        });
      }
      if (lastName) {
        Object.assign(dataRequest, {
          LAST_NAME: lastName,
        });
      }

      return this.mainHttp.get('/api/v1/user/check-problem/', dataRequest)
        .then(({ data: { data: dataRes } }) => resolve(Boolean(dataRes?.needProof)))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Оформление обратного звонка
   * @param {PayloadCallOrder} payload
   * @return {Promise<void>}
   */
  callOrder({
    phone,
    url = '',
    captchaToken,
    captchaAction,
    captchaVersion,
  }) {
    return new Promise((resolve, reject) => {
      if (!phone) {
        return reject(ERROR_CALL_ORDER.notStringPhone);
      }
      if (typeof phone !== 'string') {
        return reject(ERROR_CALL_ORDER.emptyPhone);
      }
      if (!captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      const dataRequest = {
        phone,
        captchaToken,
        captchaAction,
        captchaVersion,
      };

      if (url && url !== '') {
        Object.assign(dataRequest, {
          url,
        });
      }

      return this.mainHttp.post('/api/v1/internal/form/request-call/', dataRequest, { isFormData: true })
        .then(({ data: { data: dataRes } }) => resolve(dataRes))
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);

            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Отправляет проверочный код по СМС для подтверждения номера телефона
   * @param {string} phoneNumber
   * @return {Promise<number>}
   */
  sendSmsConfirm(phoneNumber) {
    return new Promise((resolve, reject) => this.mainHttp.get(`/api/v1/user/send-confirm-code/${phoneNumber}/`)
      .then(({ data: { data: dataRes } }) => {
        if (dataRes?.timeLeft) return resolve(dataRes.timeLeft * 1000);
        return resolve(SMS_RESENDING_TIMEOUT);
      })
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  /**
   * Новый эндпоинт - Отправляет проверочный код по СМС для подтверждения номера телефона
   * @param {object} payload
   * @param {string} [payload.phone]
   * @param {string} [payload.captchaToken]
   * @param {string} [payload.captchaAction]
   * @param {('rv2' | 'rv3')} [payload.captchaVersion]
   * @return {Promise<number>}
   */
  sendSmsConfirmNew(payload) {
    return new Promise((resolve, reject) => {
      if (!payload.phone) {
        return reject(ERROR_CALL_ORDER.notStringPhone);
      }
      if (typeof payload.phone !== 'string') {
        return reject(ERROR_CALL_ORDER.emptyPhone);
      }
      if (!payload.captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof payload.captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!payload.captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof payload.captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!payload.captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof payload.captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }
      this.mainHttp.post('/api/v1/phones/', payload)
        .then(({ data: { data: dataRes } }) => {
          if (dataRes?.token_expired_at) return resolve(dataRes.token_expired_at * 1000);
          return resolve(SMS_RESENDING_TIMEOUT);
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Новый эндпоинт - Получает информацию о номере
   * @param {object} payload
   * @param {string} [payload.phone]
   * @return {Promise<number>}
   */
  getPhoneInfo(payload) {
    return new Promise((resolve, reject) => {
      if (!payload.phone) {
        return reject(ERROR_CALL_ORDER.notStringPhone);
      }
      if (typeof payload.phone !== 'string') {
        return reject(ERROR_CALL_ORDER.emptyPhone);
      }
      this.mainHttp.get(`/api/v1/phones/?phone=${payload.phone}`)
        .then(({ data: { data: dataRes } }) => {
          if (dataRes?.data[0]?.attributes?.tokenExpiredAt) {
            return resolve(dataRes.data[0].attributes.tokenExpiredAt);
          }
          return resolve(null);
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Новый эндпоинт - Отправка введенного пользователем проверочного кода
   * @param {Object} payload
   * @param {string} [payload.phone]
   * @param {string} [payload.confirmCode]
   * @param {string} [payload.captchaToken]
   * @param {string} [payload.captchaAction]
   * @param {('rv2' | 'rv3')} [payload.captchaVersion]
   * @return {Promise<void>}
   */
  sendReceivedCodeNew(payload = {}) {
    return new Promise((resolve, reject) => {
      if (!payload.captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof payload.captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!payload.captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof payload.captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!payload.captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof payload.captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }
      this.mainHttp.post(`/api/v1/phones-confirm-verification/?code=${payload.confirmCode}`, payload)
        .then(() => resolve())
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(firstError);
          }
          return reject(e);
        });
    });
  }

  /**
   * Отправка введенного пользователем проверочного кода
   * @param {PayloadSendReceivedCode} payload
   * @return {Promise<void>}
   */
  sendReceivedCode({ phone, confirmCode }) {
    return new Promise((resolve, reject) => this.mainHttp.post('/api/v1/user/confirmation-code/', {
      PHONE: phone,
      CONFIRM_CODE: confirmCode,
    })
      .then(() => resolve())
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(firstError);
        }
        return reject(e);
      }));
  }

  sendFeedBack(payload = {}) {
    return new Promise((resolve, reject) => {
      if (!payload.name && payload.name !== 'string') reject(new ServiceError('[UserService:sendFeedBack] name обязателен для заполнения и должен быть строкой'));
      if (!payload.email && payload.email !== 'string') reject(new ServiceError('[UserService:sendFeedBack] email обязателен для заполнения и должен быть строкой'));
      if (!payload.message && payload.message !== 'string') reject(new ServiceError('[UserService:sendFeedBack] message обязателен для заполнения  и должен быть строкой'));

      if (!payload.captchaToken) {
        return reject(ERROR_CAPTCHA.emptyCaptchaToken);
      }
      if (typeof payload.captchaToken !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaToken);
      }
      if (!payload.captchaAction) {
        return reject(ERROR_CAPTCHA.emptyCaptchaAction);
      }
      if (typeof payload.captchaAction !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaAction);
      }
      if (!payload.captchaVersion) {
        return reject(ERROR_CAPTCHA.emptyCaptchaVersion);
      }
      if (typeof payload.captchaVersion !== 'string') {
        return reject(ERROR_CAPTCHA.notStringCaptchaVersion);
      }

      const dataRequest = {
        name: payload.name,
        email: payload.email,
        message: payload.message,
        captchaToken: payload.captchaToken,
        captchaAction: payload.captchaAction,
        captchaVersion: payload.captchaVersion,
      };

      if (payload.files.length) dataRequest.files = payload.files;
      return this.mainHttp.post('/api/v1/internal/form/feedback/', dataRequest, { isFormData: true })
        .then(({ data: { success } }) => {
          if (success) {
            return resolve();
          }
        })
        .catch((e) => {
          if (e.response) {
            const errors = buildErrorCollection(e.response.data);
            const [firstError] = errors;
            return reject(new ServiceError(firstError));
          }
          return reject(new ServiceError(e));
        });
    });
  }

  /**
   * Получение прогнозируемого вознаграждения за выполнение заказа
   * @param orderId - номер заказа
   * @return {Promise<WrapRewardData>}
   */
  orderReward(orderId) {
    return new Promise((resolve, reject) => this.mainHttp.get(`/api/v1/pl/user/reward/order/${orderId}/`)
      .then(({ data }) => resolve(data))
      .catch((e) => {
        if (e.response) {
          const errors = buildErrorCollection(e.response.data);
          const [firstError] = errors;
          return reject(new ServiceError(firstError));
        }
        return reject(new ServiceError(e));
      }));
  }
}
